import * as React from "react"
import { CartStructure, Product } from "../components/interfaces/SaleorTypes"
import { cloneDeep } from "lodash"
import { pushAddToCartEvent } from "../utils/datalayer"
import { CartSolutionOrder } from "../utils/models"

const LOCAL_STORAGE_CART_KEY = "satcube_cart"
const LOCAL_STORAGE_SOLUTIONS_KEY = "satcube_solutions"
const LOCAL_STORAGE_VERSION_KEY = "satcube_version"
const CURRENT_VERSION = 2 // Increment this version number when you change the data structure

export function useCreateCart() {
  const [cart, setCart] = React.useState<CartStructure[]>([])
  const [solutions, setSolutions] = React.useState<Array<CartSolutionOrder>>([])

  React.useEffect(() => {
    let cookieCart = localStorage.getItem(LOCAL_STORAGE_CART_KEY)
    let cookieSolution = localStorage.getItem(LOCAL_STORAGE_SOLUTIONS_KEY)
    let storedVersion = localStorage.getItem(LOCAL_STORAGE_VERSION_KEY)

    if (storedVersion !== String(CURRENT_VERSION)) {
      // Migrate old data structure to the new one
      if (cookieCart) {
        cookieCart = migrateCartData(cookieCart)
      }
      if (cookieSolution) {
        cookieSolution = migrateSolutionData(cookieSolution)
      }
    }

    if (cookieCart && cookieCart.length > 0) {
      setCart(JSON.parse(cookieCart))
    } else {
      setCart([])
    }

    if (cookieSolution && cookieSolution.length > 0) {
      setSolutions(JSON.parse(cookieSolution))
    } else {
      setSolutions([])
    }
  }, [])

  React.useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_CART_KEY, JSON.stringify(cart))
    localStorage.setItem(LOCAL_STORAGE_SOLUTIONS_KEY, JSON.stringify(solutions))
    localStorage.setItem(LOCAL_STORAGE_VERSION_KEY, String(CURRENT_VERSION))
  }, [cart, solutions])

  const migrateCartData = (oldCartData: string): string => {
    const oldCart = JSON.parse(oldCartData)
    const newCart = oldCart.map((item: any) => {
      // Assuming old structure had SaleorProduct instead of Product
      // SaleorProduct is a deprecated type:
      /* 
      interface SaleorProduct {
        node: Product
      } 
      */
      if (item.product && item.product.node) {
        item.product = item.product.node
      }
      return item
    })
    return JSON.stringify(newCart)
  }

  const migrateSolutionData = (oldSolutionData: string): string => {
    const oldSolutions = JSON.parse(oldSolutionData)
    const newSolutions = oldSolutions.map((solution: any) => {
      // Assuming old structure had SaleorProduct instead of Product
      if (solution.terminal && solution.terminal.node) {
        solution.terminal = solution.terminal.node
      }
      if (solution.modem && solution.modem.node) {
        solution.modem = solution.modem.node
      }
      return solution
    })
    return JSON.stringify(newSolutions)
  }

  const addToCart = (products: CartStructure[], isEditingDataPlan?: boolean) => {
    const cartCopy = cloneDeep(cart)

    products.map((item) => {
      const itemIndex = cartCopy.findIndex((x) => x.product.id == item.product.id)
      if (itemIndex >= 0) {
        cartCopy[itemIndex].amount += item.amount
      } else {
        cartCopy.push({
          product: item.product,
          amount: item.amount,
        })
      }
    })

    setCart(cartCopy)
    pushAddToCartEvent(products)
  }

  const removeFromCart = (products: CartStructure[]) => {
    const cartCopy = cloneDeep(cart)

    products.map((item) => {
      const itemIndex = cartCopy.findIndex((x) => x.product.id == item.product.id)
      if (itemIndex >= 0) {
        cartCopy[itemIndex].amount -= item.amount
        if (cartCopy[itemIndex].amount <= 0) {
          cartCopy.splice(itemIndex, 1)
        }
      }
    })

    setCart(cartCopy)
  }

  const updateItemInCart = (item: Product) => {
    const cartCopy = cloneDeep(cart)

    cartCopy.map((cartItem: CartStructure) => {
      if (cartItem.product.id == item.id) {
        cartItem.product = item
      }
    })

    setCart(cartCopy)
  }

  const clearCart = () => {
    setCart([])
    setSolutions([])
  }

  const addSolution = (solution: CartSolutionOrder) => {
    setSolutions([...solutions, solution])
  }

  const removeSolution = (solutionId: string) => {
    const filteredSolutions = solutions.filter((solution) => solution.id !== solutionId)
    setSolutions(filteredSolutions)
  }

  return {
    cart,
    addToCart,
    removeFromCart,
    updateItemInCart,
    clearCart,
    addSolution,
    solutions,
    removeSolution,
  }
}
